import { createUnifiedTheme, genPageTheme, shapes } from '@backstage/theme';

export const catTheme = createUnifiedTheme({
  defaultPageTheme: 'service',
  fontFamily: '"Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
  pageTheme: {
    home: genPageTheme({
      colors: ['#000000'],
      shape: 'linear-gradient(#000000, #000000)',
      options: { fontColor: '#ffffff' },
    }),
    service: genPageTheme({
      colors: ['#000000'],
      shape: 'linear-gradient(#000000, #000000)',
      options: { fontColor: '#ffffff' },
    }),
    apis: genPageTheme({
      colors: ['#ffcd11'],
      shape: shapes.wave1,
      options: { fontColor: '#000000' },
    }),
    tool: genPageTheme({
      colors: ['#666565'],
      shape: shapes.round,
      options: { fontColor: '#ffffff' },
    }),
    website: genPageTheme({
      colors: ['#f2f2f2'],
      shape: shapes.wave,
      options: { fontColor: '#3f3f3f' },
    }),
  },
  palette: {
    mode: 'light',

    /* -------------------------------------------------
     * PRIMARY / BRAND
     * ------------------------------------------------- */
    primary: {
      main: '#000000',          // --cat-bg-brand
      light: '#000000',         // --cat-bg-brand-hover
      dark: '#000000',          // --cat-content-knockout-brand-hover
      contrastText: '#000000',  // --cat-content-strong
    },

    /* -------------------------------------------------
     * SECONDARY (neutral emphasis)
     * ------------------------------------------------- */
    secondary: {
      main: '#a8a8a8',          // --cat-bg-strong
      light: '#777777',
      dark: '#000000',
      contrastText: '#ffffff',
    },

    /* -------------------------------------------------
     * BACKGROUND COLORS
     * ------------------------------------------------- */
    background: {
      default: '#ffffff',       // --cat-bg-default
      paper: '#f5f5f5',         // --cat-bg-subtle
    },

    /* -------------------------------------------------
     * TEXT COLORS
     * ------------------------------------------------- */
    text: {
      primary: '#3f3f3f',       // --cat-content-default
      secondary: '#777777',     // --cat-content-subtle
      disabled: '#aaaaaa',      // --cat-content-disabled
    },

    /* -------------------------------------------------
     * BACKSTAGE-SPECIFIC PALETTE ADDITIONS
     * Required by createUnifiedTheme component overrides.
     * ------------------------------------------------- */
    border: '#cccccc',
    textContrast: '#000000',
    textVerySubtle: '#a0a0a0',
    textSubtle: '#777777',
    highlight: '#dff1ff',
    errorBackground: '#ffeae6',
    warningBackground: '#ffeade',
    infoBackground: '#dff1ff',
    errorText: '#c52320',
    infoText: '#0067b8',
    warningText: '#ab470d',
    link: '#0067b8',
    linkHover: '#0078d6',
    gold: '#ffcd11',

    status: {
      ok: '#197527',
      warning: '#ab470d',
      error: '#c52320',
      pending: '#777777',
      running: '#0067b8',
      aborted: '#666565',
    },

    /* -------------------------------------------------
     * STATUS COLORS
     * ------------------------------------------------- */
    success: {
      main: '#197527',
      light: '#e6f3e5',
    },
    warning: {
      main: '#ab470d',
      light: '#ffeade',
    },
    error: {
      main: '#c52320',
      light: '#ffeae6',
    },
    info: {
      main: '#0067b8',
      light: '#dff1ff',
    },


    /* -------------------------------------------------
     * NAVIGATION SIDEBAR
     * ------------------------------------------------- */
    navigation: {
      background: '#000000',     // --cat-bg-xstrong
      color: '#f2f2f2',
      indicator: '#ffcd11',
      selectedColor: '#ffcd11',
      navItem: {
        hoverBackground: '#1f1f1f',
      },
      submenu: {
        background: '#111111',
      },
    },

    tabbar: {
      indicator: '#ffcd11',
    },

    pinSidebarButton: {
      icon: '#ffffff',
      background: '#000000',
    },

    banner: {
      info: '#0067b8',
      error: '#c52320',
      text: '#ffffff',
      link: '#ffcd11',
      warning: '#ab470d',
    },

    bursts: {
      fontColor: '#3f3f3f',
      slackChannelText: '#0067b8',
      backgroundColor: {
        default: '#ffffff',
      },
      gradient: {
        linear: 'linear-gradient(90deg, #ffcd11 0%, #ffe672 100%)',
      },
    },
  },
  components: {
    // Keyed on the component name so it applies in production too,
    // unlike CSS class selectors which are hashed in prod builds.

    // Global rule migrated from public/index.html. Backstage's catalog
    // "entity type" cards use an internal `entityTypeBox` class whose hash
    // changes on upgrade, so target it via a stable attribute selector here.
    MuiCssBaseline: {
      styleOverrides: {
        '[class*="entityTypeBox"]': {
          background: '#000 !important',
        },
      },
    },
    // Force catalog card headers to solid black (migrated from index.html).
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          background: '#000 !important',
        },
      },
    },
    // Compact input padding (migrated from index.html).
    MuiInputBase: {
      styleOverrides: {
        root: {
          padding: '1px !important',
        },
      },
    },
    // Reduced entity page header height + hidden subtitle (migrated from index.html).
    BackstageHeader: {
      styleOverrides: {
        header: {
          minHeight: '80px !important',
          height: '80px !important',
        },
        leftItemsBox: {
          minHeight: '80px !important',
          height: '80px !important',
        },
        title: {
          fontSize: '1.7rem !important',
          marginBottom: '-4px !important',
          marginTop: '23px !important',
        },
        subtitle: {
          display: 'none !important',
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          height: 'auto !important',
          minHeight: '44px !important',
          paddingTop: '6px !important',
          paddingBottom: '6px !important',
          paddingLeft: '12px !important',
          alignItems: 'center',
          textDecoration: 'none !important',
        },
        iconContainer: {
          flex: '0 0 auto !important',
          minWidth: '40px !important',
          width: '40px !important',
          marginRight: '0 !important',
        },
        label: {
          whiteSpace: 'normal !important',
          overflow: 'visible !important',
          textOverflow: 'clip !important',
          overflowWrap: 'break-word',
          wordBreak: 'normal',
          width: 'auto !important',
          minWidth: '0 !important',
          flex: '1 1 auto !important',
          textAlign: 'left',
          margin: '0 !important',
          paddingLeft: '2px',
          paddingRight: '8px',
          lineHeight: '1.2 !important',
        },
      },
    },
  },
});